import React, { useCallback, useEffect } from "react"
import cn from "classnames"
import { Trans } from "react-i18next"
import { useTranslation } from "react-i18next"
import env from "@/common/env"
import Scrollable from "@/common/components/scrollable"
import BasketSignItem, { BasketItemFragmentDoc } from "@/menu/components/basket_item"
import ClientData from "@/common/client_data"
import DotSpinner from "@/common/components/dot_spinner"
import { ArrowRightLarge, ArrowLeftLarge, CartFill, CloseLarge, CheckmarkFill } from "@/skylticons"
import ConfirmLink from "@/common/components/confirm_link"
import "../styles/basket_modal.css"
import type { TFunction } from "i18next"
import { type FragmentType, getFragment, graphql } from "@/api"
import type { BasketStatusQuery } from "@/api/graphql"

export const basketStateDoc = graphql(`
  query BasketState {
    basketState @client {
      open
      addedNotice
      excelImportNotice
      reload
    }
  }
`)

export const setBasketStateDoc = graphql(`
  mutation SetBasketState($open: Boolean!, $addedNotice: Boolean, $excelImportNotice: Boolean, $reload: Boolean) {
    setBasketState(open: $open, addedNotice: $addedNotice, excelImportNotice: $excelImportNotice, reload: $reload)
      @client {
      open
    }
  }
`)

export const BasketFragmentDoc = graphql(`
  fragment BasketModal_Order on Order {
    id
    discountPercent
    deliveryAndShippingLocalTotal
    deliveryAndShippingTaxedTotal
    localDiscountAmount
    taxedDiscountAmount
    localPrice
    localTax
    vatPercent
    localTotal
    traceable
    generatedSigns
    basketItemsTaxedTotal
    discountConverted

    basketItems {
      articleType
      totalCount
      ...BasketItem
    }

    deliveryFees {
      articleTranslation
      localTotal
      __typename
    }
  }
`)

export const basketStatusDoc = graphql(`
  query BasketStatus {
    basketStatus {
      ...BasketModal_Order
    }
  }
`)

const renderItems = (
  t: TFunction,
  showNotice: boolean,
  items: FragmentType<typeof BasketItemFragmentDoc>[],
  desktop = false
) => {
  const lastItem = items[items.length - 1]

  if (desktop) {
    return (
      <div className="items-table">
        <div className="header-wrapper">
          <table>
            <thead>
              <tr>
                <th className="index">{t("signslist.number")}</th>
                <th className="sign">{t("signslist.sign")}</th>
                <th className="name">{t("article.product_type")}</th>
                <th className="size">{t("sign.dimensions")}</th>
                <th className="fixmethod">{t("article.fix_method")}</th>
                <th className="color">{t("article.color")}</th>
                <th className="price">{t("article.price")}</th>
                <th className="count">{t("article.count")}</th>
                <th className="total">{t("article.total")}</th>
                <th className="item-controls" />
              </tr>
            </thead>
            <tbody className="items">
              {!env.TEST &&
                items.map((item, i) => (
                  <BasketSignItem
                    key={getFragment(BasketItemFragmentDoc, item).id}
                    index={i + 1}
                    item={item}
                    desktop={desktop}
                  />
                ))}
            </tbody>
          </table>
        </div>
        <div className="content-wrapper">
          <Scrollable>
            <table>
              <thead>
                <tr>
                  <th className="index">{t("signslist.number")}</th>
                  <th className="sign">{t("signslist.sign")}</th>
                  <th className="name">{t("article.product_type")}</th>
                  <th className="size">{t("sign.dimensions")}</th>
                  <th className="fixmethod">{t("article.fix_method")}</th>
                  <th className="color">{t("article.color")}</th>
                  <th className="price">{t("article.price")}</th>
                  <th className="count">{t("article.count")}</th>
                  <th className="total">{t("article.total")}</th>
                  <th className="item-controls" />
                </tr>
              </thead>
              <tbody className="items">
                {items.map((item, i) => (
                  <BasketSignItem
                    key={getFragment(BasketItemFragmentDoc, item).id}
                    index={i + 1}
                    item={item}
                    even={i % 2 !== 0}
                    scrollIntoView={item === lastItem && showNotice}
                    desktop={desktop}
                  />
                ))}
              </tbody>
            </table>
          </Scrollable>
        </div>
      </div>
    )
  }

  return (
    <Scrollable className="items">
      {items.map((item, i) => (
        <BasketSignItem
          key={getFragment(BasketItemFragmentDoc, item).id}
          index={i + 1}
          item={item}
          even={i % 2 !== 0}
          scrollIntoView={item === lastItem && showNotice}
          desktop={desktop}
        />
      ))}
    </Scrollable>
  )
}

interface ListProps {
  loading: boolean
  count?: number | string
  showNotice: boolean
  excelImportNotice?: boolean
  showEmpty?: boolean
  basketStatus: FragmentType<typeof BasketFragmentDoc> | null
  items: FragmentType<typeof BasketItemFragmentDoc>[]
  toggleBasket: () => void
  closeExcelImportNotice?: () => void
  desktop: boolean
  businessRole: boolean
  top?: number
}

const List: React.FC<ListProps> = ({
  loading,
  count,
  showNotice,
  excelImportNotice,
  basketStatus: basketStatusFragment,
  items,
  toggleBasket,
  showEmpty,
  closeExcelImportNotice,
  desktop,
  businessRole,
  top,
}) => {
  const basketStatus = getFragment(BasketFragmentDoc, basketStatusFragment)
  const { t } = useTranslation()
  const overlayClick = useCallback(
    (e: MouseEvent) => {
      if (desktop && e.target instanceof Element) {
        if (
          !e.target.closest("#signomatic-basket") &&
          !e.target.closest(".signomatic-basket-button-portal") &&
          !e.target.closest(".submit-row") &&
          !e.target.closest(".preview-modal")
        ) {
          toggleBasket()
        }
      }
    },
    [toggleBasket, desktop]
  )

  useEffect(() => {
    document.addEventListener("click", overlayClick, { capture: true })

    return () => document.removeEventListener("click", overlayClick, { capture: true })
  }, [overlayClick])

  if (loading || !basketStatus) {
    return (
      <div
        id="signomatic-basket"
        className={cn("list ordertable loading absolute xl:fixed xl:mr-6", { empty: showEmpty })}
        style={{ top: top }}
      >
        <DotSpinner />
      </div>
    )
  }

  if (items.length === 0 && showEmpty) {
    return (
      <div id="signomatic-basket" className="list ordertable empty absolute xl:fixed xl:mr-6" style={{ top: top }}>
        {!desktop && (
          <div className="cart-header">
            <span className="left">
              <CartFill />
              <span className="paragraph bold mb-0">{t("signslist.basket")}</span>
              <span className="paragraph-sm mb-0">
                {items.length} {t("checkout.items")}
              </span>
            </span>
            <button className="button button-subtle button-large icon-only mb-0" onClick={toggleBasket} type="button">
              <CloseLarge />
            </button>
          </div>
        )}
        <div className="content">
          <p className="paragraph bold center no-max-width">{t("basket.empty")}</p>
          <p className="paragraph-xs center no-max-width">
            <Trans
              i18nKey="basket.empty_text"
              values={{ designpage: t("menu.signomatic") }}
              components={{ a: <a href={`${env.languageSegment}${t("route.customer_zone")}`}>link</a> }}
            />
          </p>
          <a
            href={`${env.languageSegment}${t("route.signomatic")}`}
            className="button button-emphasis button-medium full-width mb-0"
          >
            {t("basket.cta")}
          </a>
        </div>
      </div>
    )
  }

  const deliveryFees = basketStatus.deliveryFees || []
  const totalsClass = desktop ? "paragraph-sm" : "paragraph-xs"

  return (
    <div id="signomatic-basket" className="list ordertable absolute xl:fixed xl:mr-6" style={{ top: top }}>
      {!desktop && (
        <div className="cart-header">
          <span className="left">
            <CartFill />
            <span className="paragraph bold mb-0">{t("signslist.basket")}</span>
            <span className="paragraph-sm mb-0">
              {count} {t("checkout.items")}
            </span>
          </span>
          <button className="button button-subtle button-large icon-only mb-0" onClick={toggleBasket} type="button">
            <CloseLarge />
          </button>
        </div>
      )}
      {showNotice &&
        (desktop ? (
          <div className="notice heading-three center no-max-width">{t("signomatic.added_to_basket")}</div>
        ) : (
          <div className="notice mobile">
            <CheckmarkFill />
            <span className="paragraph center paragraph-sm mb-0">{t("signomatic.added_to_basket")}</span>
          </div>
        ))}
      {excelImportNotice && (
        <div className="z-50 mx-3 mt-3 flex justify-center rounded border-1 border-green-200 bg-green-100 px-4 py-3">
          <CheckmarkFill className="mr-2 h-6 w-6 text-success" />
          <div className="flex flex-col">
            <div className="paragraph-sm bold mb-0">{t("excel_import.added_to_cart")}</div>
            <div className="paragraph-xs mb-0">{t("excel_import.please_review")}</div>
          </div>
          <button
            className="import-signs-notice-close button button-large button-subtle icon-only absolute right-7 mb-0"
            onClick={() => closeExcelImportNotice && closeExcelImportNotice()}
            type="button"
          >
            <CloseLarge />
          </button>
        </div>
      )}
      {renderItems(t, showNotice, items, desktop)}
      {basketStatus.generatedSigns && (
        <div style={{ textAlign: "right", padding: "0 10px" }}>
          <ConfirmLink
            text={t("signslist.delete_all")}
            href={`/order/remove_all/${ClientData.orderId}`}
            style={{ color: "red" }}
          />
        </div>
      )}
      <div className="totals flex touch-none flex-col border-t border-white-10 p-4 pb-3 xl:pb-4">
        <div className="prices flex justify-end">
          <div className="flex w-full justify-between xl:w-1/3">
            <div className="labels">
              {basketStatus.deliveryAndShippingLocalTotal && (
                <div className={cn(totalsClass, "mb-1 xl:mb-0")}>
                  {t("signslist.shipping")}:{deliveryFees[0] && <br />}
                  {deliveryFees[0] && deliveryFees[0].articleTranslation && (
                    <span className={cn(totalsClass, "mb-0")}>{deliveryFees[0].articleTranslation}</span>
                  )}
                </div>
              )}
              <div className="paragraph-sm bold mb-1 xl:mb-0">
                {basketStatus.vatPercent === 0 || businessRole ? t("order.price_excl_vat") : t("order.price_vat")}:
              </div>
              <div className={cn(totalsClass, "mb-1 xl:mb-0")}>
                {t("order.vat")} (<span id="basket_tax">{basketStatus.vatPercent}</span>
                %):
              </div>
              {businessRole && <div className="paragraph-sm bold mb-1 xl:mb-0">{t("order.price_vat")}:</div>}
            </div>
            <div className="values text-right">
              {basketStatus.deliveryAndShippingLocalTotal && (
                <div id="basket_delivery_price" className={cn(totalsClass, "right mb-1 xl:mb-0")}>
                  {businessRole
                    ? basketStatus.deliveryAndShippingLocalTotal
                    : basketStatus.deliveryAndShippingTaxedTotal}
                </div>
              )}
              {deliveryFees[0] && deliveryFees[0].articleTranslation && <br />}
              {businessRole && (
                <div id="basket_total_price" className={cn(totalsClass, "right mb-1 xl:mb-0")}>
                  {basketStatus.localPrice}
                </div>
              )}
              {!businessRole && (
                <>
                  <div id="basket_total_price_total" className="paragraph-sm right bold mb-1 xl:mb-0">
                    {basketStatus.localTotal}
                  </div>
                  <div id="basket_total_price_tax" className={cn(totalsClass, "right mb-1 xl:mb-0")}>
                    {basketStatus.localTax}
                  </div>
                </>
              )}
              {businessRole && (
                <>
                  <div id="basket_total_price_tax" className={cn(totalsClass, "right mb-1 xl:mb-0")}>
                    {basketStatus.localTax}
                  </div>
                  <div id="basket_total_price_total" className="paragraph-sm right bold mb-1 xl:mb-0">
                    {basketStatus.localTotal}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {basketStatus.discountPercent > 0 && (
          <div className="mt-2 flex justify-end">
            <div
              id="basket_discount_amount"
              className="blue-badge flex h-7 min-w-fit items-center rounded bg-blue-200 px-2 xl:h-8"
            >
              <div className="text-xs font-semibold text-blue-600 xl:text-base">
                {t("order.discount")} {basketStatus.discountPercent}% ({basketStatus.discountConverted})
              </div>
            </div>
          </div>
        )}
      </div>
      {desktop ? (
        <div className="buttons">
          <a
            id="basket_continue_button"
            className="button button-secondary button-medium continue"
            onClick={toggleBasket}
          >
            <ArrowLeftLarge />
            {t("toolbox.continue_shopping")}
          </a>
          {items.length > 0 && (
            <a
              id="basket_order_button"
              className="button button-primary button-medium icon-right checkout"
              href={t("route.order")}
              data-gtm="checkout-button"
            >
              {t("toolbox.checkout_button")}
              <ArrowRightLarge />
            </a>
          )}
        </div>
      ) : (
        <div className="buttons">
          {items.length > 0 && (
            <a
              id="basket_order_button"
              className="button button-primary button-medium icon-right checkout"
              href={t("route.order")}
              data-gtm="checkout-button"
            >
              {t("toolbox.checkout_button")}
            </a>
          )}
          <a
            id="basket_continue_button"
            className="button button-secondary button-medium continue mb-0"
            onClick={toggleBasket}
          >
            {t("toolbox.continue_shopping")}
          </a>
        </div>
      )}
    </div>
  )
}

interface BasketModalProps {
  showEmpty?: boolean
  businessRole: boolean
  toggleBasket: () => void
  count: number
  items: FragmentType<typeof BasketItemFragmentDoc>[]
  loading: boolean
  showAddedNotice: boolean
  closeExcelImportNotice: () => void
  open: boolean
  excelImportNotice: boolean
  data?: BasketStatusQuery
  desktop: boolean
  top?: number
}

const BasketModal: React.FC<BasketModalProps> = ({
  showEmpty,
  businessRole,
  toggleBasket,
  count,
  items,
  loading,
  showAddedNotice,
  closeExcelImportNotice,
  open,
  excelImportNotice,
  data,
  desktop,
  top,
}) => {
  return (
    <>
      {desktop ? (
        open && (
          <List
            loading={loading}
            items={items || []}
            basketStatus={data ? data.basketStatus : null}
            showNotice={showAddedNotice}
            excelImportNotice={excelImportNotice}
            toggleBasket={toggleBasket}
            showEmpty={showEmpty}
            count={count}
            closeExcelImportNotice={closeExcelImportNotice}
            desktop
            businessRole={businessRole}
            top={top}
          />
        )
      ) : (
        <div className={cn("mobile-background", { visible: open })}>
          <div className="overlay" onClick={toggleBasket}></div>
          <List
            loading={loading}
            items={items || []}
            basketStatus={data ? data.basketStatus : null}
            showNotice={showAddedNotice}
            toggleBasket={toggleBasket}
            showEmpty={showEmpty}
            count={count}
            desktop={false}
            businessRole={businessRole}
          />
        </div>
      )}
    </>
  )
}

export default BasketModal
